import Button from 'devextreme-react/button';

import SelectBox from 'devextreme-react/select-box';

import type { CSSProperties } from 'react';

import type { CalendarTarget, TimeOffStatus, TimeOffType } from '../../../../api/timeOff';

import { timeOffDetailRowLabel, timeOffDetailSection } from '../timeOffEntryPopupStyles';

import type { TimeOffEntryEditDraft } from '../timeOffEntryTypes';

import { CalendarTargetPicker } from './CalendarTargetPicker';

import { EmployeeUserPicker } from './EmployeeUserPicker';

import { TimeOffDurationFields } from './TimeOffDurationFields';



const TYPE_OPTIONS: TimeOffType[] = ['Vacation', 'Sick', 'Personal', 'Other'];

const ALL_STATUS_OPTIONS: TimeOffStatus[] = ['Pending', 'Approved', 'Rejected', 'Cancelled'];

const HR_STATUS_OPTIONS: TimeOffStatus[] = ['Pending', 'Approved', 'Rejected'];

const CALENDAR_QUESTION_LABEL = 'Which calendar should this show on?';



const calendarHintStyle: CSSProperties = {

  fontSize: 12,

  color: 'var(--ops-color-muted, #6b7280)',

  marginTop: 4,

  lineHeight: 1.35

};



interface TimeOffEntryEditContentProps {

  draft: TimeOffEntryEditDraft;

  onDraftChange: (updater: (prev: TimeOffEntryEditDraft) => TimeOffEntryEditDraft) => void;

  error: string | null;

  busy: boolean;

  itSysAdmin: boolean;

  hrCalendarTargets: CalendarTarget[];

  calendarPickerKey: number | string;

  employeePickerKey: number | string;

  onDiscard: () => void;

  onClose: () => void;

  onSave: () => void;

}



export function TimeOffEntryEditContent({

  draft,

  onDraftChange,

  error,

  busy,

  itSysAdmin,

  hrCalendarTargets,

  calendarPickerKey,

  employeePickerKey,

  onDiscard,

  onClose,

  onSave

}: TimeOffEntryEditContentProps) {

  const statusOptions = itSysAdmin ? ALL_STATUS_OPTIONS : HR_STATUS_OPTIONS;

  const hrCalendarHint =

    !itSysAdmin && hrCalendarTargets.length > 0

      ? `Posting to ${hrCalendarTargets[0].displayName} (${hrCalendarTargets[0].segment === 'Contract' ? 'Contract' : 'Company'})`

      : null;



  return (

    <div style={{ padding: '0.5rem 0.75rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ops-color-muted, #475569)' }}>

        Changes save immediately and queue calendar sync updates.

      </div>

      {error ? (

        <div

          role="alert"

          style={{

            padding: '0.5rem 0.65rem',

            borderRadius: 8,

            background: 'var(--ops-chip-danger-bg, #fef2f2)',

            color: 'var(--ops-color-danger, #991b1b)',

            fontSize: 13

          }}

        >

          {error}

        </div>

      ) : null}



      <div style={timeOffDetailSection}>

        <div style={timeOffDetailRowLabel}>Employee</div>

        <EmployeeUserPicker

          key={employeePickerKey}

          upn={draft.employeeUpn}

          displayName={draft.employeeDisplayName ?? ''}

          onChange={(employee) =>

            onDraftChange((prev) => ({

              ...prev,

              employeeUpn: employee.upn,

              employeeDisplayName: employee.displayName

            }))

          }

        />

      </div>



      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>

        <SelectBox

          items={TYPE_OPTIONS}

          value={draft.type}

          onValueChanged={(e) =>

            onDraftChange((prev) =>

              e.value !== undefined ? { ...prev, type: String(e.value) as TimeOffType } : prev

            )

          }

          stylingMode="outlined"

          label="Type"

          labelMode="outside"

          disabled={busy}

        />

        <SelectBox

          items={statusOptions}

          value={draft.status}

          onValueChanged={(e) =>

            onDraftChange((prev) =>

              e.value !== undefined ? { ...prev, status: String(e.value) as TimeOffStatus } : prev

            )

          }

          stylingMode="outlined"

          label="Status"

          labelMode="outside"

          disabled={busy}

        />

      </div>



      <TimeOffDurationFields draft={draft} onDraftChange={onDraftChange} disabled={busy} />



      <div style={timeOffDetailSection}>

        <div style={timeOffDetailRowLabel}>Notes</div>

        <textarea

          value={draft.notes ?? ''}

          onChange={(ev) =>

            onDraftChange((prev) => ({

              ...prev,

              notes: ev.target.value === '' ? null : ev.target.value

            }))

          }

          disabled={busy}

          rows={4}

          style={{

            width: '100%',

            resize: 'vertical',

            minHeight: 88,

            boxSizing: 'border-box',

            padding: '0.5rem 0.6rem',

            borderRadius: 8,

            border: '1px solid var(--ops-border, #cbd5e1)',

            fontFamily: 'inherit',

            fontSize: 14,

            background: 'var(--ops-surface, #ffffff)',

            color: 'var(--ops-color-text, #111827)'

          }}

        />

      </div>



      <div>

        {itSysAdmin ? (

          <>

            <div style={{ ...timeOffDetailRowLabel, marginBottom: 8 }}>{CALENDAR_QUESTION_LABEL}</div>

            <CalendarTargetPicker

              key={calendarPickerKey}

              value={draft.calendarTargetIds}

              onChange={(ids) => onDraftChange((prev) => ({ ...prev, calendarTargetIds: ids }))}

              disabled={busy}

            />

          </>

        ) : hrCalendarHint ? (

          <div style={calendarHintStyle}>{hrCalendarHint}</div>

        ) : (

          <div style={{ ...calendarHintStyle, color: 'var(--ops-color-danger, #b91c1c)' }}>

            No enabled calendar is available for your role. Ask an administrator to register calendar targets.

          </div>

        )}

      </div>



      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>

        <Button text="Discard" onClick={onDiscard} disabled={busy} />

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>

          <Button text="Close" disabled={busy} onClick={onClose} />

          <Button

            text={busy ? 'Saving…' : 'Save'}

            type="default"

            stylingMode="contained"

            icon="save"

            onClick={onSave}

            disabled={busy}

          />

        </div>

      </div>

    </div>

  );

}


